import { useState, useEffect, useMemo, useRef, type ComponentType } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, CALENDAR_NAV_BUTTON_CLASS } from '@safetyhub/ui';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { useGroupStore } from '../../stores/group';
import { useModalStore } from '../../stores/modal';
import { useToastStore } from '../../stores/toast';
import { usePermissions } from '../../hooks/usePermissions';
import { useWebSocketStore } from '../../stores/websocket';
import { ServerEvents, ReportDeletedNotifyPayload, ReportCreatedNotifyPayload } from '../../types/websocket';
import {
  getMonthlyInspectionSchedules,
  deleteInspection,
  startScheduledInspection,
  todayScheduledQueryRootKey,
  buildInspectionStartUrl,
  InspectionListItem,
  InspectionStatus,
  inspectionStatusLabels,
  getLocalDateString,
} from '../../lib/inspections';
import { organizationsApi, sitesApi, Site, OrganizationMember } from '../../lib/organizations';
import CreateInspectionModal from '../../components/modals/CreateInspectionModal';
import SiteFormModal from '../../components/modals/SiteFormModal';
import { ReportTypeSelectModal } from '../../components/reports/ReportTypeSelectModal';
import { ConfirmDialog } from '../../components/inspection/shared/ConfirmDialog';
import ScheduleCalendar from './components/ScheduleCalendar';
import WeekStreamCalendar, { WeekStreamCalendarHandle } from './components/WeekStreamCalendar';
import DayInspectionList from './components/DayInspectionList';
import {
  ArrowPathIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
} from '../../components/icons';
import CalendarLegend from './components/CalendarLegend';
import { buildMonthStats } from './lib/monthStats';
import {
  CalendarViewMode,
  CALENDAR_VIEW_STORAGE_KEY,
  resolveCalendarView,
  initialWeekAnchor,
  dateKeyToWeekIndex,
  dominantMonthOfWeek,
  monthsForWeekRange,
} from './lib/calendarView';

// 월/주 이동 화살표(목업 `.cal-arrow`)는 `@safetyhub/ui`의 CALENDAR_NAV_BUTTON_CLASS로 승격했다.

/**
 * 체크박스 드롭다운 — 트리거 시각층은 `components/FilterPill`의 목업 `.filter-dd`와 같은 결이다.
 * FilterPill을 그대로 쓰지 못하는 건 그쪽이 단일 선택 native `<select>`를 얹은 pill이고,
 * 여기는 검색 + 복수 선택 패널이 필요하기 때문이다. 겉모습만 맞추고 패널은 자체 구현을 유지한다.
 */
interface MultiSelectDropdownProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  selectedLabel: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}

function MultiSelectDropdown({ icon: Icon, label, selectedLabel, options, selected, onChange }: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggleOpen = () => {
    setOpen((v) => {
      if (v) setSearch('');
      return !v;
    });
  };

  const filteredOptions = search.trim()
    ? options.filter((o) => o.label.includes(search.trim()))
    : options;

  const toggleItem = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const toggleAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  // 선택 상태는 트리거 전체를 물들이는 대신 brand 값 태그가 전달한다(FilterPill 규약) —
  // 필터가 셋이라 셋 다 물들면 툴바가 brand 덩어리가 된다.
  const tagText = selected.length === 0
    ? '전체'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? '1개')
      : `${selected.length}개`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={handleToggleOpen}
        className="flex h-[38px] items-center gap-2 rounded-[10px] border border-line bg-white px-3 text-[14.5px] font-semibold text-ink-700 transition-colors hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-brand-500"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Icon className="h-3.5 w-3.5 flex-none" />
        {label}
        <span className="max-w-[140px] truncate rounded-[5px] bg-brand-50 px-1.5 py-px text-[12.5px] font-bold tracking-[.01em] text-brand-700">
          {tagText}
        </span>
        <ChevronDownIcon
          className={`-ml-0.5 h-[13px] w-[13px] flex-none text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 min-w-[200px] rounded-card border border-line bg-white shadow-card">
          {/* 검색 입력 */}
          <div className="px-2 pt-2 pb-1">
            <div className="flex items-center gap-1.5 rounded-control border border-line bg-surface-soft px-2 py-1.5 focus-within:border-brand-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-50">
              <svg className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                className="flex-1 bg-transparent text-sm text-ink-900 placeholder-ink-400 outline-none min-w-0"
                placeholder="검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="py-1">
            {/* 전체 선택/해제 — 검색어 없을 때만 표시 */}
            {!search.trim() && options.length > 0 && (
              <>
                <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover text-sm text-ink-700 font-semibold">
                  <input
                    type="checkbox"
                    className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={selected.length === options.length && options.length > 0}
                    onChange={toggleAll}
                  />
                  전체 {label}
                </label>
                <div className="border-t border-line-soft my-1" />
              </>
            )}
            {options.length === 0 && (
              <p className="px-3 py-2 text-sm text-ink-400">{selectedLabel} 없음</p>
            )}
            {filteredOptions.length === 0 && options.length > 0 && (
              <p className="px-3 py-2 text-sm text-ink-400">검색 결과 없음</p>
            )}
            {filteredOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover text-sm font-medium text-ink-700"
              >
                <input
                  type="checkbox"
                  className="rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggleItem(opt.value)}
                />
                <span className="truncate max-w-[160px]">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SchedulesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentMembership } = useAuthStore();
  const currentGroupId = useGroupStore((s) => s.currentGroupId);
  const { openCreateInspection } = useModalStore();
  const { showToast } = useToastStore();
  const { canManageInspections } = usePermissions();

  const queryClient = useQueryClient();
  const { subscribe, isConnected, isAuthenticated } = useWebSocketStore();

  const companyId = currentMembership?.companyId ?? '';

  // 보고서 생성/삭제 이벤트 구독 → 스케줄 데이터 refetch
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !companyId) return;

    const unsubscribeCreated = subscribe<ReportCreatedNotifyPayload>(
      ServerEvents.REPORT_CREATED_NOTIFY,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
        }
      }
    );

    const unsubscribeDeleted = subscribe<ReportDeletedNotifyPayload>(
      ServerEvents.REPORT_DELETED_NOTIFY,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });
        }
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeDeleted();
    };
  }, [isConnected, isAuthenticated, companyId, subscribe, queryClient]);

  // URL 파라미터에서 초기값 읽기
  const today = new Date();
  const monthParam = searchParams.get('month'); // "YYYY-MM"
  const dateParam = searchParams.get('date');   // "YYYY-MM-DD"

  const validDateParam = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : null;

  // month 파라미터 없으면 date 파라미터의 연/월 사용, 그것도 없으면 오늘
  const initialYear = monthParam
    ? parseInt(monthParam.split('-')[0], 10)
    : validDateParam
      ? parseInt(validDateParam.split('-')[0], 10)
      : today.getFullYear();
  const initialMonth = monthParam
    ? parseInt(monthParam.split('-')[1], 10) - 1
    : validDateParam
      ? parseInt(validDateParam.split('-')[1], 10) - 1
      : today.getMonth();

  // 현재 월 상태
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth); // 0-indexed

  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // 달력 뷰 모드 (월간/주간): URL 파라미터 우선 → localStorage → 월간
  const [view, setView] = useState<CalendarViewMode>(() =>
    resolveCalendarView(
      searchParams.get('view'),
      typeof window !== 'undefined' ? localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY) : null,
    )
  );

  // 주간 뷰 중심 주 인덱스 (date 파라미터 > month 1일 > 오늘)
  const [centerWeek, setCenterWeek] = useState(() =>
    dateKeyToWeekIndex(initialWeekAnchor(monthParam, dateParam, todayKey))
  );

  const weekCalRef = useRef<WeekStreamCalendarHandle>(null);

  // 선택된 날짜
  const [selectedDate, setSelectedDate] = useState<string | null>(() => {
    if (validDateParam) return validDateParam;
    return todayKey;
  });

  // 축소 뷰에서 표시할 중심 날짜 (스크롤 이동 시 이 값만 변경, selectedDate 유지)
  const [collapsedCenterDate, setCollapsedCenterDate] = useState<string | null>(null);

  // URL 동기화: year/month/selectedDate/view 변경 시 URL 업데이트
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('month', `${year}-${String(month + 1).padStart(2, '0')}`);
    newParams.set('view', view);
    if (selectedDate) {
      newParams.set('date', selectedDate);
    } else {
      newParams.delete('date');
    }
    setSearchParams(newParams, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedDate, view]);

  // 모바일/폴드 달력 축소 여부 (날짜 선택 시 3주 뷰)
  const isMobileCalendarCollapsed = selectedDate !== null;

  // 화면 높이가 낮을 때 축소 뷰 전환 (700px 이하)
  const [isShortScreen, setIsShortScreen] = useState(() =>
    typeof window !== 'undefined' && window.innerHeight <= 700
  );

  // PC 여부 감지 (마우스/트랙패드 = fine pointer)
  const [isDesktopPointer, setIsDesktopPointer] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches
  );

  useEffect(() => {
    const checkHeight = () => {
      setIsShortScreen(window.innerHeight <= 700);
    };
    const pointerMq = window.matchMedia('(pointer: fine)');
    const checkPointer = (e: MediaQueryListEvent) => {
      setIsDesktopPointer(e.matches);
    };
    window.addEventListener('resize', checkHeight);
    pointerMq.addEventListener('change', checkPointer);
    return () => {
      window.removeEventListener('resize', checkHeight);
      pointerMq.removeEventListener('change', checkPointer);
    };
  }, []);

  // 달력 슬라이드 방향 (월 이동 시)
  const [calendarSlideDirection, setCalendarSlideDirection] = useState<'left' | 'right' | null>(null);

  // 필터 상태 (복수 선택)
  const [filterSiteIds, setFilterSiteIds] = useState<string[]>([]);
  const [filterInspectorIds, setFilterInspectorIds] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>(() => {
    const initialStatus = searchParams.get('status');
    const VALID_STATUSES = Object.values(InspectionStatus) as readonly string[];
    return initialStatus && VALID_STATUSES.includes(initialStatus) ? [initialStatus] : [];
  });

  // 그룹 전환 시 현장 필터를 비운다 — 이전 그룹의 현장 id가 남으면 group∩site 교집합으로
  // 0건이 된다. 최초 마운트는 건너뛰고 실제 전환에서만 초기화한다.
  const prevGroupIdRef = useRef(currentGroupId);
  useEffect(() => {
    if (prevGroupIdRef.current === currentGroupId) return;
    prevGroupIdRef.current = currentGroupId;
    setFilterSiteIds([]);
  }, [currentGroupId]);

  // 월 문자열 "YYYY-MM"
  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  // fetch 대상 월 목록: 월간=현재 월 1개, 주간=가시 주 범위가 걸치는 달 + 앞뒤 1개월 프리페치
  const monthsToFetch = useMemo<string[]>(() => {
    if (view === 'week') return monthsForWeekRange(centerWeek - 3, centerWeek + 4, 1);
    return [monthStr];
  }, [view, centerWeek, monthStr]);

  // 스케줄 목록 조회 — 서버 API는 월 단위이므로 월별 병렬 조회 후 병합 (월 단위 캐시 공유)
  const { inspections, isLoading } = useQueries({
    queries: monthsToFetch.map((m) => ({
      // filterStatuses는 클라이언트 측 필터 (서버 파라미터 아님) → queryKey에 포함하지 않음
      queryKey: ['inspectionSchedules', companyId, m, filterSiteIds, filterInspectorIds, currentGroupId],
      queryFn: () =>
        getMonthlyInspectionSchedules(
          companyId,
          m,
          filterSiteIds.length > 0 ? filterSiteIds.join(',') : undefined,
          filterInspectorIds.length > 0 ? filterInspectorIds.join(',') : undefined,
          currentGroupId
        ),
      enabled: !!companyId,
    })),
    combine: (results) => {
      const merged: InspectionListItem[] = [];
      const seen = new Set<string>();
      for (const r of results) {
        if (!r.data?.success || !r.data.data) continue;
        for (const insp of r.data.data) {
          if (seen.has(insp.id)) continue;
          seen.add(insp.id);
          merged.push(insp);
        }
      }
      return {
        inspections: merged,
        // 초기 로딩만 스피너 표시 — 주간 스크롤로 새 달이 추가될 땐 기존 데이터 유지.
        // isLoading(=pending+fetching)이라 enabled:false(companyId 미로드)에선 false
        isLoading: results.length > 0 && results.every((r) => r.isLoading),
      };
    },
  });

  // 스케줄 데이터 갱신 (모든 월 캐시 무효화)
  const refetchSchedules = () =>
    queryClient.invalidateQueries({ queryKey: ['inspectionSchedules'] });

  // 현장 목록 조회
  const { data: sites = [] } = useQuery<Site[]>({
    queryKey: ['sites', companyId, currentGroupId],
    queryFn: () => sitesApi.findAll(companyId, currentGroupId),
    enabled: !!companyId,
  });

  // 멤버 목록 조회
  const { data: members = [] } = useQuery<OrganizationMember[]>({
    queryKey: ['organizationMembers', companyId],
    queryFn: () => organizationsApi.getMembers(companyId),
    enabled: !!companyId,
  });

  // 상태 필터 적용된 점검 목록
  const filteredInspections = useMemo(() => {
    if (filterStatuses.length === 0) return inspections;
    return inspections.filter((insp) => filterStatuses.includes(insp.status));
  }, [inspections, filterStatuses]);

  // 범례 바 통계 — 달력이 그리는 것과 같은 목록(필터 적용 후)에서 뽑는다.
  // 원본 inspections로 세면 필터를 걸었을 때 달력과 숫자가 갈린다.
  const monthStats = useMemo(
    () => buildMonthStats(filteredInspections, monthStr),
    [filteredInspections, monthStr],
  );

  // 선택된 날짜의 점검 목록
  const selectedDateInspections = useMemo(() => {
    if (!selectedDate) return [];
    return filteredInspections.filter((insp) => {
      const rawDate = insp.scheduledAt ?? insp.createdAt;
      return getLocalDateString(new Date(rawDate)) === selectedDate;
    });
  }, [filteredInspections, selectedDate]);

  // 이전 월 이동
  const goToPrevMonth = () => {
    setCalendarSlideDirection('right');
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else {
      setMonth((m) => m - 1);
    }
    setSelectedDate(null);
    setCollapsedCenterDate(null);
  };

  // 다음 월 이동
  const goToNextMonth = () => {
    setCalendarSlideDirection('left');
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else {
      setMonth((m) => m + 1);
    }
    setSelectedDate(null);
    setCollapsedCenterDate(null);
  };

  // 오늘로 이동
  const goToToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
    const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setSelectedDate(nowKey);
    setCollapsedCenterDate(null);
    if (view === 'week') {
      weekCalRef.current?.scrollToWeek(dateKeyToWeekIndex(nowKey));
    }
  };

  // Chevron 네비게이션: 월간=월 이동, 주간=주 이동
  const handleNavPrev = () => {
    if (view === 'week') {
      weekCalRef.current?.scrollByWeeks(-1);
    } else {
      goToPrevMonth();
    }
  };
  const handleNavNext = () => {
    if (view === 'week') {
      weekCalRef.current?.scrollByWeeks(1);
    } else {
      goToNextMonth();
    }
  };

  // 뷰 토글 (월간 ↔ 주간): localStorage에 선호 유지 + 현재 위치 기준 재앵커
  const handleViewChange = (next: CalendarViewMode) => {
    if (next === view) return;
    if (next === 'week') {
      // 현재 표시 중인 달(선택 날짜가 그 달에 있으면 그 날짜)이 속한 주를 중심으로
      const anchor = selectedDate && selectedDate.startsWith(monthStr) ? selectedDate : `${monthStr}-01`;
      setCenterWeek(dateKeyToWeekIndex(anchor));
    }
    setView(next);
    try {
      localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, next);
    } catch {
      // localStorage 사용 불가 환경(사파리 프라이빗 등)에서는 URL 파라미터로만 유지
    }
  };

  // 주간 뷰 스크롤로 중심 주 변경 → 헤더 제목 월(4일 이상 걸친 달) 동기화
  const handleCenterWeekChange = (weekIndex: number) => {
    setCenterWeek(weekIndex);
    const dominant = dominantMonthOfWeek(weekIndex);
    if (dominant.year !== year) setYear(dominant.year);
    if (dominant.month !== month) setMonth(dominant.month);
  };

  // 월간 뷰 제스처 패닝 (휠/스와이프/드래그) → 월 스냅 이동
  const handleMonthPan = (direction: 1 | -1) => {
    if (direction === 1) goToNextMonth();
    else goToPrevMonth();
  };

  // 달력에서 이전/다음 달 날짜 클릭 시 월 전환
  const handleMonthChange = (newYear: number, newMonth: number) => {
    setYear(newYear);
    setMonth(newMonth);
  };

  // 날짜 클릭 핸들러: selectedDate 변경 + collapsedCenterDate 리셋
  const handleDateClick = (date: string | null) => {
    setSelectedDate(date);
    setCollapsedCenterDate(null); // 클릭 시 center를 새 날짜로 리셋 (null이면 selectedDate 사용)
  };

  // 축소 뷰(3주)에서 스크롤로 주 이동: collapsedCenterDate만 변경, selectedDate는 유지
  const handleWeekScroll = (delta: number) => {
    const base = collapsedCenterDate ?? selectedDate ?? `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const [y, m, d] = base.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + delta);
    const newCenter = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setCollapsedCenterDate(newCenter);

    // 네비게이션 월: 중앙 주(일~토) 7일 중 더 많은 날을 차지하는 달로 결정
    const center = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dow = center.getDay();
    const sunday = new Date(center);
    sunday.setDate(center.getDate() - dow);

    const monthCount: Record<string, number> = {};
    for (let i = 0; i < 7; i++) {
      const dd = new Date(sunday);
      dd.setDate(sunday.getDate() + i);
      const key = `${dd.getFullYear()}-${dd.getMonth()}`;
      monthCount[key] = (monthCount[key] || 0) + 1;
    }

    const dominant = Object.entries(monthCount).sort((a, b) => b[1] - a[1])[0];
    const [dominantYear, dominantMonth] = dominant[0].split('-').map(Number);
    if (dominantMonth !== month || dominantYear !== year) {
      setYear(dominantYear);
      setMonth(dominantMonth);
    }
  };

  // 점검 삭제
  const handleDeleteInspection = async (id: string) => {
    if (!companyId) return;
    try {
      const res = await deleteInspection(id, companyId);
      if (res.success) {
        showToast('점검이 삭제되었습니다.', 'success');
        await refetchSchedules();
      } else {
        showToast('점검 삭제에 실패했습니다.', 'error');
      }
    } catch {
      showToast('점검 삭제에 실패했습니다.', 'error');
    }
  };

  // 점검 시작 확인 모달
  const [startConfirmId, setStartConfirmId] = useState<string | null>(null);

  // 보고서 생성 모달
  const [reportModalInspection, setReportModalInspection] = useState<{ id: string; siteId: string } | null>(null);

  // 점검 시작 (SCHEDULED → IN_PROGRESS): 모달만 열기
  const handleStartScheduled = (id: string) => {
    setStartConfirmId(id);
  };

  // 점검 시작 확인: 실제 API 호출
  const handleConfirmStart = async () => {
    if (!startConfirmId || !companyId) return;
    try {
      const res = await startScheduledInspection(startConfirmId, companyId);
      if (res.success) {
        showToast('점검이 시작되었습니다.', 'success');
        await refetchSchedules();
        // 오늘 예정된 점검 위젯(대시보드)에서 즉시 제거 (WebSocket emit 누락/지연 대비)
        // 키는 `lib/inspections.ts`의 루트 헬퍼 — 리터럴을 적으면 그 문자열을 고칠 때 조용히
        // 아무것도 무효화하지 않는다(2026-10-05).
        queryClient.invalidateQueries({ queryKey: todayScheduledQueryRootKey() });
        navigate(buildInspectionStartUrl(startConfirmId));
      } else {
        showToast(res.error?.message || '점검 시작에 실패했습니다.', 'error');
      }
    } catch {
      showToast('점검 시작에 실패했습니다.', 'error');
    } finally {
      setStartConfirmId(null);
    }
  };

  const handleCreateReport = (id: string) => {
    const insp = inspections.find((i) => i.id === id);
    if (insp) {
      setReportModalInspection({ id: insp.id, siteId: insp.siteId });
    }
  };

  // 점검 예약 (날짜 클릭 → CreateInspectionModal 열기)
  const handleCreateInspection = (date: string) => {
    openCreateInspection(date);
  };

  const MONTH_NAMES = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

  return (
    <div>
      {/* 1행: 제목 + 페이지 액션(새로고침 · 요약보기 · 점검 예약) */}
      <div className="flex items-end justify-between gap-6 mb-3 sm:mb-4">
        <div>
          <h1 className="text-lg font-bold text-ink-900 sm:text-[26px] sm:font-extrabold sm:tracking-tight">스케줄 관리</h1>
          <p className="mt-1 hidden text-[15px] font-medium text-ink-500 sm:block">
            점검 일정을 한눈에 확인하고 예약을 관리합니다.
          </p>
        </div>
        <div className="flex flex-none items-center gap-2">
          <button
            type="button"
            onClick={() => refetchSchedules()}
            className="hidden sm:grid h-[38px] w-[38px] place-items-center rounded-[10px] border border-line bg-white text-ink-700 transition-colors hover:bg-surface-hover hover:text-ink-900"
            aria-label="새로고침"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>

          {/* N월 요약보기 — 날짜 선택(collapsed) 시 표시. 주간 뷰에선 모바일(월간 고정)에서만 */}
          {isMobileCalendarCollapsed && (
            <button
              onClick={() => { setSelectedDate(null); setCollapsedCenterDate(null); }}
              className={`${view === 'week' ? 'sm:hidden ' : ''}rounded-[9px] border border-brand-100 bg-brand-50 px-3 py-1.5 text-xs font-bold text-brand-700 transition-colors hover:border-brand-500 hover:bg-brand-100 sm:h-[34px] sm:py-0`}
            >
              {MONTH_NAMES[month]} 요약보기
            </button>
          )}

          {canManageInspections && (
            <Button
              variant="primary"
              className="flex-none"
              onClick={() => openCreateInspection(selectedDate ?? undefined)}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              점검 예약
            </Button>
          )}
        </div>
      </div>

      {/* 2행: 월 네비게이션 + 필터 + 뷰 토글 — sm+에서만 목업 툴바 카드로 감싼다(모바일 여백 불변) */}
      <div className="flex flex-wrap items-center gap-3 mb-4 sm:rounded-card-lg sm:border sm:border-line sm:bg-white sm:p-3.5 sm:shadow-card-sm">
        {/* 월/주 네비게이션 */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleNavPrev}
            className={CALENDAR_NAV_BUTTON_CLASS}
            aria-label={view === 'week' ? '이전 주' : '이전 달'}
          >
            <ChevronLeftIcon className="w-3.5 h-3.5" />
          </button>
          <h2 className="min-w-[100px] px-1 text-center text-base font-extrabold tracking-tight tabular-nums text-ink-900 sm:text-lg">
            {year}년 {MONTH_NAMES[month]}
          </h2>
          <button
            onClick={handleNavNext}
            className={CALENDAR_NAV_BUTTON_CLASS}
            aria-label={view === 'week' ? '다음 주' : '다음 달'}
          >
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={goToToday}
            className="ml-1 h-[34px] rounded-[9px] border border-line bg-white px-3 text-[13px] font-semibold text-ink-700 transition-colors hover:bg-surface-hover hover:text-ink-900"
          >
            오늘
          </button>
        </div>

        {/* 구분선 */}
        <div className="hidden sm:block w-px h-6 bg-line mx-1" />

        <MultiSelectDropdown
          icon={BuildingIcon}
          label="현장"
          selectedLabel="현장"
          options={sites.map((site) => ({ value: site.id, label: site.name }))}
          selected={filterSiteIds}
          onChange={setFilterSiteIds}
        />

        <MultiSelectDropdown
          icon={UserIcon}
          label="담당자"
          selectedLabel="담당자"
          options={members.filter((m): m is typeof m & { userId: string } => m.userId != null).map((member) => ({ value: member.userId, label: member.user?.name ?? '(이름 없음)' }))}
          selected={filterInspectorIds}
          onChange={setFilterInspectorIds}
        />

        <MultiSelectDropdown
          icon={CheckCircleIcon}
          label="상태"
          selectedLabel="상태"
          options={Object.values(InspectionStatus).map((status) => ({
            value: status,
            label: inspectionStatusLabels[status],
          }))}
          selected={filterStatuses}
          onChange={setFilterStatuses}
        />

        {(filterSiteIds.length > 0 || filterInspectorIds.length > 0 || filterStatuses.length > 0) && (
          <Button
            variant="secondary"
            onClick={() => { setFilterSiteIds([]); setFilterInspectorIds([]); setFilterStatuses([]); }}
          >
            필터 초기화
          </Button>
        )}

        {/* 구분선 */}
        <div className="hidden sm:block w-px h-6 bg-line mx-1" />

        {/* 뷰 토글 (월간/주간) — 주간 뷰는 데스크톱(sm+) 전용 */}
        <div className="hidden sm:flex h-[38px] items-center rounded-control border border-line bg-surface-soft p-[3px]">
          <button
            onClick={() => handleViewChange('month')}
            className={`h-[30px] px-3 text-sm rounded-[7px] transition-colors ${
              view === 'month'
                ? 'bg-white font-bold text-brand-600 shadow-card-sm'
                : 'font-semibold text-ink-500 hover:text-ink-800'
            }`}
            aria-pressed={view === 'month'}
          >
            월간
          </button>
          <button
            onClick={() => handleViewChange('week')}
            className={`h-[30px] px-3 text-sm rounded-[7px] transition-colors ${
              view === 'week'
                ? 'bg-white font-bold text-brand-600 shadow-card-sm'
                : 'font-semibold text-ink-500 hover:text-ink-800'
            }`}
            aria-pressed={view === 'week'}
          >
            주간
          </button>
        </div>
      </div>

      {/* 범례 + 이번 달 통계 — 로딩 중에는 0건이 아니라 숫자를 감춘다 */}
      <div className="mb-4">
        <CalendarLegend stats={isLoading ? null : monthStats} />
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      )}

      {!isLoading && (
        <>
          {/* 모바일 (sm 미만): 달력 + 하단 DayInspectionList */}
          <div className="sm:hidden flex flex-col gap-4">
            <ScheduleCalendar
              year={year}
              month={month}
              inspections={filteredInspections}
              selectedDate={selectedDate}
              onDateClick={handleDateClick}
              onMonthChange={handleMonthChange}
              collapsed={isMobileCalendarCollapsed}
              centerDate={collapsedCenterDate}
              onWeekChange={handleWeekScroll}
              externalSlideDirection={calendarSlideDirection}
            />
            <DayInspectionList
              date={selectedDate}
              inspections={selectedDateInspections}
              allInspections={filteredInspections}
              currentMonth={{ year, month }}
              onCreateInspection={handleCreateInspection}
              onDeleteInspection={handleDeleteInspection}
              onStartScheduled={handleStartScheduled}
              onCreateReport={handleCreateReport}
            />
          </div>

          {view === 'month' ? (
            <>
              {/* sm~lg 미만 (폴드/PC 좁은 창): PC+높이 충분하면 월 전체, 터치/높이 부족하면 3주 축소 */}
              <div className="hidden sm:flex 2xl:hidden flex-col gap-4">
                <div className="w-full">
                  <ScheduleCalendar
                    year={year}
                    month={month}
                    inspections={filteredInspections}
                    selectedDate={selectedDate}
                    onDateClick={handleDateClick}
                    onMonthChange={handleMonthChange}
                    collapsed={!(isDesktopPointer && !isShortScreen) && isMobileCalendarCollapsed}
                    centerDate={!(isDesktopPointer && !isShortScreen) ? collapsedCenterDate : undefined}
                    onWeekChange={handleWeekScroll}
                    externalSlideDirection={calendarSlideDirection}
                    onMonthPan={handleMonthPan}
                  />
                </div>

                {/* 일별 목록 패널 */}
                <div className="w-full">
                  <DayInspectionList
                    date={selectedDate}
                    inspections={selectedDateInspections}
                    allInspections={filteredInspections}
                    currentMonth={{ year, month }}
                    onCreateInspection={handleCreateInspection}
                    onDeleteInspection={handleDeleteInspection}
                    onStartScheduled={handleStartScheduled}
                    onCreateReport={handleCreateReport}
                  />
                </div>
              </div>

              {/* 2xl 이상 (PC): 2열 레이아웃, 화면 높이 충분하면 월간 뷰, 낮으면 3주 축소 뷰 */}
              <div className="hidden 2xl:flex gap-4 items-start">
                <div className="w-full 2xl:w-2/3 min-w-0">
                  <ScheduleCalendar
                    year={year}
                    month={month}
                    inspections={filteredInspections}
                    selectedDate={selectedDate}
                    onDateClick={handleDateClick}
                    onMonthChange={handleMonthChange}
                    collapsed={isShortScreen && isMobileCalendarCollapsed}
                    centerDate={isShortScreen ? collapsedCenterDate : undefined}
                    onWeekChange={handleWeekScroll}
                    externalSlideDirection={calendarSlideDirection}
                    onMonthPan={handleMonthPan}
                  />
                </div>

                {/* 일별 목록 패널 — sticky 고정 */}
                <div className="w-full 2xl:w-1/3 flex-shrink-0 2xl:sticky 2xl:top-6">
                  <DayInspectionList
                    date={selectedDate}
                    inspections={selectedDateInspections}
                    allInspections={filteredInspections}
                    currentMonth={{ year, month }}
                    onCreateInspection={handleCreateInspection}
                    onDeleteInspection={handleDeleteInspection}
                    onStartScheduled={handleStartScheduled}
                    onCreateReport={handleCreateReport}
                  />
                </div>
              </div>
            </>
          ) : (
            /* 주간 뷰 (sm+ 전용): 연속 주 스트림 + 일별 목록 (2xl에서 2열) */
            <div className="hidden sm:flex flex-col 2xl:flex-row gap-4 2xl:items-start">
              <div className="w-full 2xl:w-2/3 min-w-0">
                <WeekStreamCalendar
                  ref={weekCalRef}
                  initialWeek={centerWeek}
                  inspections={filteredInspections}
                  selectedDate={selectedDate}
                  onDateClick={handleDateClick}
                  onCenterWeekChange={handleCenterWeekChange}
                />
              </div>

              {/* 일별 목록 패널 — 2xl에서 sticky 고정 */}
              <div className="w-full 2xl:w-1/3 2xl:flex-shrink-0 2xl:sticky 2xl:top-6">
                <DayInspectionList
                  date={selectedDate}
                  inspections={selectedDateInspections}
                  allInspections={filteredInspections}
                  currentMonth={{ year, month }}
                  onCreateInspection={handleCreateInspection}
                  onDeleteInspection={handleDeleteInspection}
                  onStartScheduled={handleStartScheduled}
                  onCreateReport={handleCreateReport}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* 점검 생성 모달 */}
      <CreateInspectionModal
        onSuccess={() => refetchSchedules()}
      />

      {/* 현장 생성 모달 (FAB에서 호출 시) */}
      <SiteFormModal
        onSuccess={() => {
          if (companyId) {
            // 접두사 매칭이라 ['sites', companyId, groupId?] 캐시가 모두 무효화된다.
            queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
          }
        }}
      />

      {/* 점검 시작 확인 모달 */}
      <ConfirmDialog
        isOpen={!!startConfirmId}
        onClose={() => setStartConfirmId(null)}
        onConfirm={handleConfirmStart}
        title="점검 시작"
        message="이 점검을 시작하시겠습니까?"
        confirmText="시작"
        cancelText="취소"
        confirmStyle="primary"
      />

      {/* 보고서 생성 모달 */}
      {reportModalInspection && (
        <ReportTypeSelectModal
          isOpen={!!reportModalInspection}
          inspectionId={reportModalInspection.id}
          companyId={companyId || ''}
          siteId={reportModalInspection.siteId}
          onClose={() => setReportModalInspection(null)}
        />
      )}
    </div>
  );
}
